import './order-type-address-nav-bar.css';

import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Data } from '../../../models/data';
import { Util } from '../../../utils/util';
import { OrderType, Pages } from '../../../types/enums';
import { OrderService } from '../../../services/order-service';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Manages the order type and address navigation bar.
 */
export class OrderTypeAddressNavBar {
    private _element: HTMLElement | null = null;
    private _elementOrderType: HTMLElement | null = null;
    private _elementOrderAddress: HTMLElement | null = null;
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _localizationService: LocalizationService;
    private readonly _orderService: OrderService;

    /**
     * Initializes the order type and address navigation bar.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The data service.
     * @param {OrderService} orderService - The order service.
     */
    constructor(eventBus: EventEmitter, data: Data, localizationService: LocalizationService, orderService: OrderService) {
        this._element = document.getElementById("nav_order");
        this._elementOrderType = document.getElementById("nav_order_type");
        this._elementOrderAddress = document.getElementById("nav_order_address");
        this._eventBus = eventBus;
        this._data = data;
        this._localizationService = localizationService;
        this._orderService = orderService;
        this._eventListeners();
        this._eventBusListeners();
    }

    /**
     * Sets up event listeners for user interactions.
     * Clicking the navbar redirects to the locations page.
     * @private
     */
    private _eventListeners(): void {
        this._element?.addEventListener("click", () => this._onElementClick());
        Util.addKeyboardActivation(this._element);
    }

    private _onElementClick(): void {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { forChangingStore: true });
    }

    /**
     * Sets up event bus listeners for changes in order type and location.
     * @private
     */
    private _eventBusListeners(): void {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, () => this.render());
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages) => {
            if (!this._element)
                return;

            this.render();

            const pages = [Pages.MENU, Pages.MENU_VIEW_ALL, Pages.ORDERS, Pages.FAVORITES];

            if (pages.includes(page))
                Util.showElement(this._element);
            else
                Util.hideElement(this._element);
        });
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => this.render());
        this._eventBus.on(EventTypes.STORE_CHANGED, () => this._setAddress());
    }

    /**
     * Updates the navbar with the order type and address.
     */
    public render = (): void => {
        this._elementOrderType && (this._elementOrderType.textContent = this._getOrderTypeName().name);
        this._setAddress();
    }

    private readonly _setAddress = (): void => {
        this._elementOrderAddress && (this._elementOrderAddress.textContent = this._getAddress())
    }

    /**
     * Retrieves the name of the current order type.
     * @private
     * @returns {{ name: string, ltagj: string }} The order type name and its translation key.
     */
    private _getOrderTypeName(): { name: string, ltagj: string } {
        const orderType = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === this._orderService.order.getProperty("OrderTypeID"));
        if (!orderType)
            return { name: "", ltagj: "" };

        const orderTypeName = this._localizationService.GetName(orderType.Names);
        return { name: orderTypeName, ltagj: Util.toLtagj(orderType.Names) };
    }

    /**
     * Retrieves the formatted address for the order, based on the order type.
     * @private
     * @returns {string} The formatted address.
     */
    private _getAddress(): string {
        let address = "";

        if (this._orderService.order.getProperty("OrderTypeID") === OrderType.DELIVERY) {
            const deliveryAddress = this._orderService.order.getProperty("Address");

            if (deliveryAddress) {
                address = Util.formatAddressObject({
                    StreetNo: deliveryAddress.StreetNo,
                    Address1: deliveryAddress.StreetName,
                    Address2: deliveryAddress.Address2,
                    Address3: deliveryAddress.Address3,
                    Address4: deliveryAddress.Address4,
                    Address5: deliveryAddress.Address5,
                    City: deliveryAddress.City,
                    State: deliveryAddress.State,
                    ZipCode: deliveryAddress.Zip,
                    CountryID: deliveryAddress.CountryID || 1,
                    AddressTypeID: 0
                }, this._data.getBrandProperty("Countries")).address1;
            }
        } else {
            const store = this._data.getProperty("StoreInfo");

            address = Util.formatAddressObject({
                StreetNo: store.streetNumber,
                Address1: store.address1,
                Address2: store.address2,
                Address3: store.address3,
                Address4: store.address4,
                Address5: store.address5,
                City: store.city,
                State: store.state,
                ZipCode: store.zip,
                CountryID: store.countryId || 1,
                AddressTypeID: 0
            }, this._data.getBrandProperty("Countries")).address1;
        }

        return address;
    }
}